import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { companiesApi, myOrdersApi } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { entityConfig } from '../../utils/entityTypeConfig';
import ClientGstTab from './ClientGstTab';
import {
  Building2, ArrowLeft, Plus, Users, KeyRound, MapPin, CreditCard,
  Pencil, Trash2, Save, X, Loader2, ShoppingCart, ShieldCheck, Eye, EyeOff,
  ChevronRight, FileText, UserPlus, ExternalLink, Hash, Calendar, Mail, Phone,
  Activity, Clock, Send, RefreshCw, Receipt
} from 'lucide-react';

// ==================== Reusable Modal ====================
const Modal = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-[#0f1611] border border-emerald-900/40 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()} data-testid={testId}>
      <div className="p-5 border-b border-emerald-900/40 flex items-center justify-between sticky top-0 bg-[#0f1611] z-10">
        <h3 className="text-lg font-semibold text-white font-serif">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid={`${testId}-close`}>
          <X className="w-5 h-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const FormField = ({ label, value, onChange, type = 'text', placeholder = '', testId, required = false }) => (
  <div>
    <label className="text-xs text-slate-400 block mb-1">{label}{required && <span className="text-emerald-400 ml-0.5">*</span>}</label>
    <Input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="bg-[#080b09] border-emerald-900/30 text-white text-sm focus-visible:ring-emerald-500"
      data-testid={testId} />
  </div>
);

// ==================== Director Modal ====================
const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="text-xs text-slate-400 block mb-1">{label}</label>
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 rounded-md bg-[#080b09] border border-emerald-900/30 text-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
      data-testid={testId}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);
const DIN_OPTIONS = [
  { value: 'not_applied', label: 'Not Applied' },
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
];
const DSC_OPTIONS = [
  { value: 'not_applied', label: 'Not Applied' },
  { value: 'applied', label: 'Applied' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
];

const DirectorModal = ({ director, onSave, onClose, saving, entityCfg }) => {
  const [form, setForm] = useState({
    full_name: director?.full_name || '',
    email: director?.email || '',
    phone: director?.phone || '',
    pan_number: director?.pan_number || '',
    aadhaar_number: director?.aadhaar_number || '',
    din_number: director?.din_number || '',
    din_status: director?.din_status || 'not_applied',
    dsc_status: director?.dsc_status || 'not_applied',
    date_of_birth: director?.date_of_birth || '',
    address: director?.address || '',
    father_name: director?.father_name || '',
    nationality: director?.nationality || 'Indian',
    occupation: director?.occupation || '',
    profit_share_ratio: director?.profit_share_ratio ?? '',
  });
  const isEdit = !!director?.id;
  const canSave = form.full_name && form.email && form.phone;
  const label = entityCfg?.director_label || 'Director';
  const showProfitShare = !!entityCfg?.has_profit_share;
  return (
    <Modal title={isEdit ? `Edit ${label}` : `Add ${label}`} onClose={onClose} testId="director-modal">
      <div className="p-5 space-y-3">
        <FormField label="Full Name" required value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testId="director-full-name" />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Email" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="director-email" />
          <FormField label="Phone" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="director-phone" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="PAN Number" value={form.pan_number} onChange={(v) => setForm({ ...form, pan_number: v })} testId="director-pan" placeholder="ABCDE1234F" />
          <FormField label="DIN Number" value={form.din_number} onChange={(v) => setForm({ ...form, din_number: v })} testId="director-din" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="DIN Status" value={form.din_status} onChange={(v) => setForm({ ...form, din_status: v })} options={DIN_OPTIONS} testId="director-din-status" />
          <SelectField label="DSC Status" value={form.dsc_status} onChange={(v) => setForm({ ...form, dsc_status: v })} options={DSC_OPTIONS} testId="director-dsc-status" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Aadhaar Number" value={form.aadhaar_number} onChange={(v) => setForm({ ...form, aadhaar_number: v })} testId="director-aadhaar" />
          <FormField label="Date of Birth" type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} testId="director-dob" />
        </div>
        <FormField label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="director-address" />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Father's Name" value={form.father_name} onChange={(v) => setForm({ ...form, father_name: v })} testId="director-father" />
          <FormField label="Occupation" value={form.occupation} onChange={(v) => setForm({ ...form, occupation: v })} testId="director-occupation" />
        </div>
        {showProfitShare && (
          <FormField
            label="Profit & Loss Sharing Ratio (%)"
            type="number"
            value={form.profit_share_ratio}
            onChange={(v) => setForm({ ...form, profit_share_ratio: v })}
            placeholder="e.g. 50"
            testId="director-profit-share"
          />
        )}
      </div>
      <div className="p-5 border-t border-emerald-900/40 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-300 bg-transparent hover:bg-slate-800">Cancel</Button>
        <Button
          onClick={() => onSave(
            { ...form, profit_share_ratio: form.profit_share_ratio === '' ? null : parseFloat(form.profit_share_ratio) },
            isEdit ? director.id : null
          )}
          disabled={saving || !canSave}
          className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium" data-testid="save-director-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          {isEdit ? 'Update' : `Add ${label}`}
        </Button>
      </div>
    </Modal>
  );
};

// ==================== Shareholder Modal ====================
const ShareholderModal = ({ sh, directors = [], entityCfg, onSave, onClose, saving }) => {
  const isEdit = !!sh?.id;
  const buildForm = (src) => ({
    shareholder_name: src?.shareholder_name || src?.full_name || '',
    shareholder_email: src?.shareholder_email || src?.email || '',
    phone: src?.phone || '',
    pan_number: src?.pan_number || '',
    aadhaar_number: src?.aadhaar_number || '',
    number_of_shares: src?.number_of_shares || 0,
    shareholder_type: src?.shareholder_type || 'individual',
    din_number: src?.din_number || '',
    din_status: src?.din_status || 'not_applied',
    dsc_status: src?.dsc_status || 'not_applied',
    dsc_expiry_date: src?.dsc_expiry_date || '',
    address: src?.address || '',
    father_name: src?.father_name || '',
    date_of_birth: src?.date_of_birth || '',
  });
  const [form, setForm] = useState(buildForm(sh));
  const [copySource, setCopySource] = useState('manual');
  const canSave = form.shareholder_name && form.number_of_shares > 0;

  const handleFetch = (val) => {
    setCopySource(val);
    if (val === 'manual') return;
    const d = directors.find(x => x.id === val);
    if (d) setForm({ ...buildForm(d), number_of_shares: form.number_of_shares });
  };

  const buildPayload = () => {
    const p = { ...form };
    ['shareholder_email', 'phone', 'pan_number', 'aadhaar_number', 'din_number',
     'address', 'father_name', 'date_of_birth', 'dsc_expiry_date'].forEach((k) => {
      if (!p[k]) delete p[k];
    });
    return p;
  };
  const dirLabel = (entityCfg?.director_label || 'Director').toLowerCase();
  const shLabel = entityCfg?.shareholder_label || 'Shareholder';
  return (
    <Modal title={isEdit ? `Edit ${shLabel}` : `Add ${shLabel}`} onClose={onClose} testId="shareholder-modal">
      <div className="p-5 space-y-3">
        {/* Fetch from Director — same UX as admin. Skip when editing to avoid confusion. */}
        {!isEdit && (
          <div className="p-2.5 rounded border border-emerald-500/30 bg-emerald-500/[0.04]">
            <label className="text-[11px] uppercase tracking-wider text-emerald-400 font-semibold">Start from</label>
            <select
              value={copySource}
              onChange={(e) => handleFetch(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 bg-[#0f1611] border border-emerald-500/40 rounded text-white text-sm focus:outline-none focus:border-emerald-400"
              data-testid="shareholder-source-select"
            >
              <option value="manual">Manually enter details</option>
              {directors.length > 0 && <option disabled>──── Fetch from {dirLabel} ────</option>}
              {directors.map(d => (
                <option key={d.id} value={d.id}>
                  {d.full_name}{d.din_number ? ` · DIN ${d.din_number}` : ''}{d.pan_number ? ` · ${d.pan_number}` : ''}
                </option>
              ))}
            </select>
            {copySource !== 'manual' && (
              <p className="text-[11px] text-emerald-300 mt-1">Pre-filled from {dirLabel}. Just set the number of shares below.</p>
            )}
          </div>
        )}
        <FormField label="Full Name" required value={form.shareholder_name} onChange={(v) => setForm({ ...form, shareholder_name: v })} testId="shareholder-full-name" />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Email" type="email" value={form.shareholder_email} onChange={(v) => setForm({ ...form, shareholder_email: v })} testId="shareholder-email" />
          <FormField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="shareholder-phone" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Number of Shares" required type="number" value={form.number_of_shares}
            onChange={(v) => setForm({ ...form, number_of_shares: parseInt(v) || 0 })} testId="shareholder-shares" />
          <FormField label="PAN Number" value={form.pan_number} onChange={(v) => setForm({ ...form, pan_number: v })} testId="shareholder-pan" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Aadhaar Number" value={form.aadhaar_number} onChange={(v) => setForm({ ...form, aadhaar_number: v })} testId="shareholder-aadhaar" />
          <FormField label="Date of Birth" type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} testId="shareholder-dob" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="DIN Number" value={form.din_number} onChange={(v) => setForm({ ...form, din_number: v })} testId="shareholder-din" />
          <FormField label="DSC Expiry Date" type="date" value={form.dsc_expiry_date} onChange={(v) => setForm({ ...form, dsc_expiry_date: v })} testId="shareholder-dsc-expiry" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="DIN Status" value={form.din_status} onChange={(v) => setForm({ ...form, din_status: v })} options={DIN_OPTIONS} testId="shareholder-din-status" />
          <SelectField label="DSC Status" value={form.dsc_status} onChange={(v) => setForm({ ...form, dsc_status: v })} options={DSC_OPTIONS} testId="shareholder-dsc-status" />
        </div>
        <FormField label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="shareholder-address" />
        <FormField label="Father's Name" value={form.father_name} onChange={(v) => setForm({ ...form, father_name: v })} testId="shareholder-father" />
      </div>
      <div className="p-5 border-t border-emerald-900/40 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-300 bg-transparent hover:bg-slate-800">Cancel</Button>
        <Button onClick={() => onSave(buildPayload(), isEdit ? sh.id : null)} disabled={saving || !canSave}
          className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium" data-testid="save-shareholder-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          {isEdit ? 'Update' : `Add ${shLabel}`}
        </Button>
      </div>
    </Modal>
  );
};

// ==================== Company Edit Modal ====================
const CompanyEditModal = ({ company, section, onSave, onClose, saving }) => {
  // section: 'overview' | 'office' | 'capital'
  const cap = company.capital_structure || {};
  const [form, setForm] = useState(
    section === 'overview'
      ? {
          company_name: company.company_name || '',
          state: company.state || '',
          date_of_incorporation: company.date_of_incorporation || '',
          cin_llpin: company.cin_llpin || '',
          tan_number: company.tan_number || '',
          gstin: company.gstin || '',
        }
      : section === 'office'
      ? {
          registered_address: company.registered_address || '',
          business_activity: company.business_activity || '',
          police_station_jurisdiction: company.police_station_jurisdiction || '',
          company_email: company.company_email || '',
          company_phone: company.company_phone || '',
          google_location_lat: company.google_location_lat || '',
          google_location_lng: company.google_location_lng || '',
        }
      : {
          authorized_capital: cap.authorized_capital ?? '',
          paid_up_capital: cap.paid_up_capital ?? '',
          share_face_value: cap.share_face_value ?? '',
          contribution: cap.contribution ?? '',
        }
  );
  const cfg = entityConfig(company?.entity_type);
  const title = section === 'overview' ? 'Edit Company Overview'
    : section === 'office' ? 'Edit Registered Office'
    : 'Edit Capital Structure';

  const handleSave = () => {
    if (section === 'capital') {
      // LLP / Partnership use a single Total Contribution field. Everything else
      // keeps the classic authorised / paid-up / face-value trio.
      if (cfg.capital_kind === 'contribution') {
        onSave({
          capital_structure: {
            contribution: parseFloat(form.contribution) || 0,
          },
        });
      } else {
        onSave({
          capital_structure: {
            authorized_capital: parseFloat(form.authorized_capital) || 0,
            paid_up_capital: parseFloat(form.paid_up_capital) || 0,
            share_face_value: parseFloat(form.share_face_value) || 0,
          },
        });
      }
    } else {
      onSave(form);
    }
  };

  return (
    <Modal title={title} onClose={onClose} testId={`company-edit-${section}-modal`}>
      <div className="p-5 space-y-3">
        {section === 'overview' && (
          <>
            <FormField label="Company Name" required value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testId="ce-company-name" />
            <div className="grid grid-cols-2 gap-3">
              <FormField label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testId="ce-state" />
              <FormField label="Date of Incorporation" type="date" value={form.date_of_incorporation} onChange={(v) => setForm({ ...form, date_of_incorporation: v })} testId="ce-doi" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="CIN / LLPIN" value={form.cin_llpin} onChange={(v) => setForm({ ...form, cin_llpin: v })} testId="ce-cin" placeholder="U12345MH2021PTC012345" />
              <FormField label="TAN Number" value={form.tan_number} onChange={(v) => setForm({ ...form, tan_number: v })} testId="ce-tan" placeholder="ABCD12345E" />
            </div>
            <FormField label="GSTIN" value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v.toUpperCase() })} testId="ce-gstin" placeholder="27AABCT1332L1ZK" />
            <p className="text-[11px] text-slate-500">PAN is auto-derived from GSTIN (characters 3–12).</p>
          </>
        )}
        {section === 'office' && (
          <>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Registered Address</label>
              <textarea value={form.registered_address} onChange={(e) => setForm({ ...form, registered_address: e.target.value })}
                rows={3} placeholder="Full registered office address..."
                className="w-full bg-[#080b09] border border-emerald-900/30 rounded-md text-white text-sm p-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="ce-address" />
            </div>
            <FormField label="Business Activity" value={form.business_activity} onChange={(v) => setForm({ ...form, business_activity: v })} testId="ce-activity" placeholder="e.g. IT Consulting Services" />
            <FormField label="Police Station Jurisdiction" value={form.police_station_jurisdiction} onChange={(v) => setForm({ ...form, police_station_jurisdiction: v })} testId="ce-police" />
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Company Email" type="email" value={form.company_email} onChange={(v) => setForm({ ...form, company_email: v })} testId="ce-email" />
              <FormField label="Company Phone" value={form.company_phone} onChange={(v) => setForm({ ...form, company_phone: v })} testId="ce-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Google Location (Lat)" value={form.google_location_lat} onChange={(v) => setForm({ ...form, google_location_lat: v })} testId="ce-lat" placeholder="e.g. 19.0760" />
              <FormField label="Google Location (Lng)" value={form.google_location_lng} onChange={(v) => setForm({ ...form, google_location_lng: v })} testId="ce-lng" placeholder="e.g. 72.8777" />
            </div>
            <p className="text-[11px] text-slate-500">Location & Jurisdiction data is shared with admin for compliance filings.</p>
          </>
        )}
        {section === 'capital' && cfg.capital_kind === 'contribution' && (
          <>
            <FormField label="Total Contribution (₹)" type="number" value={form.contribution}
              onChange={(v) => setForm({ ...form, contribution: v })}
              testId="ce-contribution" placeholder="100000" />
            <p className="text-[11px] text-slate-500">Total capital contributed by all {cfg.directors_label.toLowerCase()}. Individual contributions are captured on each partner&apos;s profile.</p>
          </>
        )}
        {section === 'capital' && cfg.capital_kind === 'company' && (
          <>
            <FormField label="Authorized Capital (₹)" type="number" value={form.authorized_capital} onChange={(v) => setForm({ ...form, authorized_capital: v })} testId="ce-auth-cap" placeholder="100000" />
            <FormField label="Paid-up Capital (₹)" type="number" value={form.paid_up_capital} onChange={(v) => setForm({ ...form, paid_up_capital: v })} testId="ce-paid-cap" placeholder="100000" />
            <FormField label="Share Face Value (₹)" type="number" value={form.share_face_value} onChange={(v) => setForm({ ...form, share_face_value: v })} testId="ce-face-val" placeholder="10" />
            <p className="text-[11px] text-slate-500">Paid-up capital cannot exceed Authorized capital. Contact admin for equity restructuring.</p>
          </>
        )}
      </div>
      <div className="p-5 border-t border-emerald-900/40 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-300 bg-transparent hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium" data-testid="save-company-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Save
        </Button>
      </div>
    </Modal>
  );
};

// ==================== Credential Modal ====================
const CredentialModal = ({ cred, companyId, onSave, onClose, saving }) => {
  const [form, setForm] = useState({
    credential_name: cred?.credential_name || '',
    url: cred?.url || '',
    username: cred?.username || '',
    password: cred?.password || '',
    notes: cred?.notes || '',
  });
  const isEdit = !!cred?.id;
  return (
    <Modal title={isEdit ? 'Edit Credential' : 'Add Credential'} onClose={onClose} testId="credential-modal">
      <div className="p-5 space-y-3">
        <FormField label="Label" required value={form.credential_name} onChange={(v) => setForm({ ...form, credential_name: v })} testId="cred-label" placeholder="e.g. GST Portal Login" />
        <FormField label="Portal URL" value={form.url} onChange={(v) => setForm({ ...form, url: v })} testId="cred-url" placeholder="https://..." />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Username" value={form.username} onChange={(v) => setForm({ ...form, username: v })} testId="cred-username" />
          <FormField label="Password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="cred-password" />
        </div>
        <FormField label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="cred-notes" />
      </div>
      <div className="p-5 border-t border-emerald-900/40 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-300 bg-transparent hover:bg-slate-800">Cancel</Button>
        <Button onClick={() => onSave(form, isEdit ? cred.id : null)} disabled={saving || !form.credential_name}
          className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium" data-testid="save-credential-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          {isEdit ? 'Update' : 'Add Credential'}
        </Button>
      </div>
    </Modal>
  );
};

// ==================== Section Card ====================
const SectionCard = ({ icon: Icon, title, actionLabel, onAction, actionIcon: ActionIcon = Plus, statusPill, children, testId }) => (
  <div className="bg-[#0f1611] border border-emerald-900/30 rounded-xl p-5 sm:p-6" data-testid={testId}>
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2.5">
        <Icon className="w-5 h-5 text-emerald-400" />
        <h3 className="text-lg font-serif text-white">{title}</h3>
      </div>
      <div className="flex items-center gap-2">
        {statusPill}
        {onAction && (
          <Button size="sm" onClick={onAction} className="bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 h-8" data-testid={`${testId}-add`}>
            <ActionIcon className="w-3.5 h-3.5 mr-1" /> {actionLabel}
          </Button>
        )}
      </div>
    </div>
    {children}
  </div>
);

const InfoField = ({ label, value, icon: Icon }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-emerald-400" />}
      <p className="text-sm text-white">{value || <span className="text-slate-600 italic">Not set</span>}</p>
    </div>
  </div>
);

const EmptyRow = ({ text }) => (
  <div className="text-center py-6 border border-dashed border-emerald-900/30 rounded-lg">
    <p className="text-sm text-slate-500">{text}</p>
  </div>
);

// ==================== Status Badge ====================
const STATUS_CFG = {
  din: {
    active: { cls: 'bg-emerald-500/20 text-emerald-400', label: 'Active' },
    not_applied: { cls: 'bg-slate-600/30 text-slate-400', label: 'Not Applied' },
    deactivated: { cls: 'bg-red-500/20 text-red-400', label: 'Deactivated' },
  },
  dsc: {
    active: { cls: 'bg-emerald-500/20 text-emerald-400', label: 'Active' },
    applied: { cls: 'bg-blue-500/20 text-blue-400', label: 'Applied' },
    not_applied: { cls: 'bg-slate-600/30 text-slate-400', label: 'Not Applied' },
    expired: { cls: 'bg-red-500/20 text-red-400', label: 'Expired' },
  },
};
const StatusBadge = ({ status, type }) => {
  const c = STATUS_CFG[type]?.[status] || STATUS_CFG[type]?.not_applied || { cls: 'bg-slate-600/30 text-slate-400', label: status || 'N/A' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${c.cls}`}>{c.label}</span>;
};

// ==================== Table wrapper ====================
const DataTable = ({ headers, rows, empty, testId }) => (
  <div className="overflow-x-auto rounded-lg border border-emerald-900/20" data-testid={testId}>
    {rows.length === 0 ? (
      <div className="p-6 text-center text-sm text-slate-500">{empty}</div>
    ) : (
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-[#080b09]">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">{h}</th>
            ))}
            <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium w-24">Actions</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    )}
  </div>
);
const Cell = ({ children, mono = false, className = '' }) => (
  <td className={`px-3 py-3 text-slate-200 ${mono ? 'font-mono text-xs' : 'text-sm'} ${className}`}>
    {children || <span className="text-slate-600">—</span>}
  </td>
);

// ==================== Main Component ====================
const CompanyDetailView = ({ companyId, onBack, orders: allOrders = [], userRole }) => {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [directors, setDirectors] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [creds, setCreds] = useState([]);
  const [activities, setActivities] = useState([]);
  const [renewalDir, setRenewalDir] = useState(null); // director object when renewal modal open
  const [renewalType, setRenewalType] = useState('dsc');
  const [renewalMsg, setRenewalMsg] = useState('');
  const [submittingRenewal, setSubmittingRenewal] = useState(false);
  const [companyEdit, setCompanyEdit] = useState(null); // { section: 'overview'|'office' } or null
  const [loading, setLoading] = useState(true);
  const [dirModal, setDirModal] = useState(null);
  const [shModal, setShModal] = useState(null);
  const [credModal, setCredModal] = useState(null);
  const [showPw, setShowPw] = useState({});
  const [modalSaving, setModalSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'gst'
  const [gstRegs, setGstRegs] = useState([]);

  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [comp, dirs, shs, credRes, actRes, gstRes] = await Promise.all([
        companiesApi.getById(companyId),
        companiesApi.getDirectors(companyId),
        companiesApi.getShareholders(companyId),
        api.get(`/credentials/company/${companyId}`).then((r) => r.data).catch(() => []),
        api.get(`/client-portal/company/${companyId}/activity`).then((r) => r.data?.activities || []).catch(() => []),
        api.get(`/client/companies/${companyId}/gst`).then((r) => r.data?.registrations || []).catch(() => []),
      ]);
      setCompany(comp);
      setDirectors(dirs);
      setShareholders(shs);
      setCreds(credRes || []);
      setActivities(actRes);
      setGstRegs(gstRes || []);
    } catch (e) {
      toast.error('Failed to load company');
      onBack?.();
    } finally {
      setLoading(false);
    }
  }, [companyId, onBack]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const companyOrders = allOrders.filter((o) => o.company_id === companyId);
  const recentOrders = [...companyOrders].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 4);

  const saveDirector = async (form, dirId) => {
    setModalSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === '' || payload[k] == null) delete payload[k]; });
      payload.full_name = form.full_name;
      payload.email = form.email;
      payload.phone = form.phone;
      if (dirId) {
        await api.put(`/directors/${dirId}`, payload);
        toast.success('Director updated');
      } else {
        // DirectorCreate schema does NOT include din_status/dsc_status — server defaults them.
        // Strip so pydantic doesn't reject extra fields.
        const { din_status, dsc_status, ...createPayload } = payload;
        const res = await api.post('/directors', createPayload);
        await api.post(`/companies/${companyId}/directors/${res.data.id}`);
        // If user changed din/dsc status on create form, PATCH them right after
        if (din_status !== 'not_applied' || dsc_status !== 'not_applied') {
          await api.put(`/directors/${res.data.id}`, { din_status, dsc_status });
        }
        toast.success('Director added');
      }
      setDirModal(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save director');
    } finally { setModalSaving(false); }
  };

  const deleteDirector = async (dir) => {
    if (!window.confirm(`Remove director "${dir.full_name}"? This will delete their record from the pool.`)) return;
    try {
      await api.delete(`/directors/${dir.id}`);
      toast.success('Director removed');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete director');
    }
  };

  const saveShareholder = async (form, shId) => {
    setModalSaving(true);
    try {
      if (shId) {
        await companiesApi.updateShareholder(shId, form);
        toast.success('Shareholder updated');
      } else {
        await companiesApi.addShareholder(companyId, form);
        toast.success('Shareholder added');
      }
      setShModal(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save shareholder');
    } finally { setModalSaving(false); }
  };

  const saveCredential = async (form, credId) => {
    setModalSaving(true);
    try {
      if (credId) {
        await api.put(`/credentials/${credId}`, form);
        toast.success('Credential updated');
      } else {
        await api.post('/credentials', { ...form, entity_type: 'company', entity_id: companyId });
        toast.success('Credential added');
      }
      setCredModal(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save credential');
    } finally { setModalSaving(false); }
  };

  const deleteCredential = async (cred) => {
    if (!window.confirm(`Delete credential "${cred.credential_name}"?`)) return;
    try {
      await api.delete(`/credentials/${cred.id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const openRenewal = (dir, type) => {
    setRenewalDir(dir);
    setRenewalType(type);
    setRenewalMsg('');
  };
  const submitRenewal = async () => {
    if (!renewalDir) return;
    setSubmittingRenewal(true);
    try {
      const label = renewalType === 'dsc' ? 'DSC' : 'DIN';
      await api.post('/renewal-request', {
        company_id: companyId,
        director_id: renewalDir.id,
        request_type: renewalType,
        message: renewalMsg || undefined,
      });
      toast.success(`${label} renewal request sent to admin`);
      setRenewalDir(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit request');
    } finally { setSubmittingRenewal(false); }
  };

  const saveCompany = async (patch) => {
    setModalSaving(true);
    try {
      const clean = { ...patch };
      Object.keys(clean).forEach((k) => { if (clean[k] === '' || clean[k] == null) delete clean[k]; });
      await companiesApi.update(companyId, clean);
      toast.success('Company updated');
      setCompanyEdit(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update company');
    } finally { setModalSaving(false); }
  };

  const formatCurrency = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  // Derive PAN from GSTIN — Indian GSTIN format: 2-digit state + 10-char PAN + 1-char entity + Z + 1-char check
  const derivePan = (gstin) => (gstin && typeof gstin === 'string' && gstin.length >= 12 ? gstin.substring(2, 12).toUpperCase() : null);
  const fmtRelative = (iso) => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr${hrs !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading || !company) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const cap = company.capital_structure || {};
  const cfg = entityConfig(company?.entity_type);
  const activeStatusPill = (label) => (
    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
      {label}
    </span>
  );

  return (
    <div data-testid="company-detail-view">
      {/* Back link */}
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-emerald-400 mb-4 transition-colors" data-testid="back-to-companies">
        <ArrowLeft className="w-4 h-4" /> Back to Companies
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#0f1611] border border-emerald-500/30 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-3xl font-serif text-white leading-tight">{company.company_name}</h1>
            <p className="text-sm text-slate-400 mt-0.5">{company.entity_type?.replace(/_/g, ' ')}</p>
          </div>
        </div>
      </div>

      {/* Tab strip — GST only appears when the company has registrations */}
      {gstRegs.length > 0 && (
        <div className="flex items-center gap-1 border-b border-emerald-900/30 mb-6" data-testid="company-tabs">
          <button onClick={() => setActiveTab('details')} data-testid="company-tab-details"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'details' ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-white'}`}>
            <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4" /> Company Details</span>
          </button>
          <button onClick={() => setActiveTab('gst')} data-testid="company-tab-gst"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'gst' ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-white'}`}>
            <span className="inline-flex items-center gap-1.5"><Receipt className="w-4 h-4" /> GST ({gstRegs.length})</span>
          </button>
        </div>
      )}

      {activeTab === 'gst' && gstRegs.length > 0 && (
        <ClientGstTab companyId={companyId} registrations={gstRegs} onReload={fetchData} />
      )}

      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${activeTab === 'gst' ? 'hidden' : ''}`}>
        {/* Left/Main column */}
        <div className="lg:col-span-2 space-y-5">
          {/* Company Overview */}
          <SectionCard icon={FileText} title="Company Overview" testId="section-overview"
            actionLabel="Edit" actionIcon={Pencil} onAction={() => setCompanyEdit({ section: 'overview' })}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoField label="State" value={company.state} icon={MapPin} />
              <InfoField label="Date of Incorporation" value={fmtDate(company.date_of_incorporation)} icon={Calendar} />
              <InfoField label="CIN / LLPIN" value={company.cin_llpin} />
              <InfoField label="TAN" value={company.tan_number} />
              <InfoField label="GSTIN" value={company.gstin} />
              <InfoField label="PAN" value={derivePan(company.gstin)} />
            </div>
          </SectionCard>

          {/* Registered Office */}
          <SectionCard icon={ShieldCheck} title="Registered Office"
            actionLabel="Edit" actionIcon={Pencil} onAction={() => setCompanyEdit({ section: 'office' })}
            statusPill={company.registered_address ? activeStatusPill('On File') : null} testId="section-registered-office">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <InfoField label="Registered Address" value={company.registered_address} icon={MapPin} />
              </div>
              <InfoField label="Business Activity" value={company.business_activity} />
              <InfoField label="Police Station Jurisdiction" value={company.police_station_jurisdiction} />
              <InfoField label="Company Email" value={company.company_email} icon={Mail} />
              <InfoField label="Company Phone" value={company.company_phone} icon={Phone} />
              <InfoField label="Google Location (Lat)" value={company.google_location_lat} />
              <InfoField label="Google Location (Lng)" value={company.google_location_lng} />
            </div>
          </SectionCard>

          {/* Capital Structure — hidden entirely for entity types that don't have one (Proprietorship). */}
          {cfg.capital_kind !== 'none' && (
            <SectionCard icon={CreditCard} title="Capital Structure" testId="section-capital"
              actionLabel="Edit" actionIcon={Pencil} onAction={() => setCompanyEdit({ section: 'capital' })}>
              {cfg.capital_kind === 'contribution' ? (
                <div>
                  <InfoField label="Total Contribution" value={cap.contribution ? formatCurrency(cap.contribution) : null} />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Combined contribution of all {cfg.directors_label.toLowerCase()}. LLP/Partnership entities don&apos;t issue shares.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <InfoField label="Authorized Capital" value={cap.authorized_capital ? formatCurrency(cap.authorized_capital) : null} />
                  <InfoField label="Paid-up Capital" value={cap.paid_up_capital ? formatCurrency(cap.paid_up_capital) : null} />
                  <InfoField label="Share Face Value" value={cap.share_face_value ? formatCurrency(cap.share_face_value) : null} />
                </div>
              )}
            </SectionCard>
          )}

          {/* Directors / Partners / Proprietor */}
          <SectionCard icon={Users} title={`${cfg.directors_label} (${directors.length})`}
            actionLabel={`Add ${cfg.director_label}`} onAction={() => setDirModal({})} testId="section-directors">
            <DataTable headers={['Name', 'Phone', 'Email', 'PAN', 'DIN', 'DIN Status', 'DSC Status']} testId="directors-table"
              empty="No directors linked yet — click Add Director to link one."
              rows={directors.map((d) => (
                <tr key={d.id} className="border-t border-emerald-900/20 hover:bg-emerald-500/5 transition-colors" data-testid={`director-row-${d.id}`}>
                  <Cell><span className="text-white font-medium">{d.full_name}</span></Cell>
                  <Cell mono>{d.phone}</Cell>
                  <Cell className="text-slate-300">{d.email}</Cell>
                  <Cell mono>{d.pan_number}</Cell>
                  <Cell mono>{d.din_number}</Cell>
                  <Cell><StatusBadge status={d.din_status} type="din" /></Cell>
                  <Cell><StatusBadge status={d.dsc_status} type="dsc" /></Cell>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    {(d.dsc_status === 'expired' || d.dsc_status === 'active') && (
                      <button onClick={() => openRenewal(d, 'dsc')}
                        className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-[11px] rounded bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition-colors"
                        title="Request DSC renewal from admin"
                        data-testid={`director-row-${d.id}-renew-dsc`}>
                        <RefreshCw className="w-3 h-3" /> Renew DSC
                      </button>
                    )}
                    {cfg.show_shareholders && (
                      <button
                        onClick={() => setShModal({
                          shareholder_name: d.full_name,
                          shareholder_email: d.email,
                          phone: d.phone,
                          pan_number: d.pan_number,
                          aadhaar_number: d.aadhaar_number,
                          din_number: d.din_number,
                          din_status: d.din_status,
                          dsc_status: d.dsc_status,
                          dsc_expiry_date: d.dsc_expiry_date,
                          address: d.address,
                          father_name: d.father_name,
                          date_of_birth: d.date_of_birth,
                          shareholder_type: 'individual',
                          number_of_shares: 0,
                        })}
                        className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-[11px] rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
                        title={`Copy this ${cfg.director_label.toLowerCase()}'s info into a new ${cfg.shareholder_label.toLowerCase()}`}
                        data-testid={`director-row-${d.id}-copy-to-shareholder`}
                      >
                        <UserPlus className="w-3 h-3" /> Add as {cfg.shareholder_label}
                      </button>
                    )}
                    <button onClick={() => setDirModal(d)} className="p-1.5 text-slate-400 hover:text-white transition-colors" data-testid={`director-row-${d.id}-edit`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                      <button onClick={() => deleteDirector(d)} className="p-1.5 text-slate-400 hover:text-red-400 transition-colors ml-1" data-testid={`director-row-${d.id}-delete`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            />
            {!isAdmin && directors.length > 0 && (
              <p className="text-[11px] text-slate-500 pt-2">Contact admin to remove a director.</p>
            )}
          </SectionCard>

          {/* Shareholders — hidden for LLP/Partnership/Proprietorship (they use contribution instead of shares) */}
          {cfg.show_shareholders && (
          <SectionCard icon={Users} title={`${cfg.shareholder_label}s (${shareholders.length})`}
            actionLabel={`Add ${cfg.shareholder_label}`} onAction={() => setShModal({})} testId="section-shareholders">
            <DataTable headers={['Name', 'Phone', 'Email', 'PAN', 'Shares', 'Holding %']} testId="shareholders-table"
              empty="No shareholders added yet — click Add Shareholder."
              rows={shareholders.map((s) => (
                <tr key={s.id} className="border-t border-emerald-900/20 hover:bg-emerald-500/5 transition-colors" data-testid={`shareholder-row-${s.id}`}>
                  <Cell><span className="text-white font-medium">{s.shareholder_name}</span></Cell>
                  <Cell mono>{s.phone}</Cell>
                  <Cell className="text-slate-300">{s.shareholder_email}</Cell>
                  <Cell mono>{s.pan_number}</Cell>
                  <Cell>{s.number_of_shares}</Cell>
                  <Cell>{s.percent_holding != null ? `${s.percent_holding}%` : ''}</Cell>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setShModal(s)} className="p-1.5 text-slate-400 hover:text-white transition-colors" data-testid={`shareholder-row-${s.id}-edit`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            />
          </SectionCard>
          )}

          {/* Credentials */}
          <SectionCard icon={KeyRound} title={`Credentials (${creds.length})`}
            actionLabel="Add Credential" onAction={() => setCredModal({})} testId="section-credentials">
            {creds.length === 0 ? <EmptyRow text="No credentials saved yet — click Add Credential." /> : (
              <div className="space-y-2.5">
                {creds.map((c) => (
                  <div key={c.id} className="p-4 bg-[#080b09] border border-emerald-900/20 rounded-lg" data-testid={`credential-row-${c.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-white">{c.credential_name}</p>
                          {c.url && (
                            <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                        <div className="mt-1.5 text-sm text-slate-300 space-y-0.5">
                          {c.username && <p className="text-xs text-slate-400">User: <span className="font-mono text-slate-200">{c.username}</span></p>}
                          {c.password && (
                            <p className="text-xs text-slate-400 flex items-center gap-2">
                              Pass: <span className="font-mono text-slate-200">{showPw[c.id] ? c.password : '••••••••'}</span>
                              <button onClick={() => setShowPw((p) => ({ ...p, [c.id]: !p[c.id] }))} className="text-slate-500 hover:text-emerald-400" data-testid={`toggle-pw-${c.id}`}>
                                {showPw[c.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            </p>
                          )}
                          {c.notes && <p className="text-xs text-slate-500 mt-1">{c.notes}</p>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => setCredModal(c)} className="p-1.5 text-slate-400 hover:text-white" data-testid={`edit-credential-${c.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteCredential(c)} className="p-1.5 text-slate-400 hover:text-red-400" data-testid={`delete-credential-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Activity Feed */}
          <SectionCard icon={Activity} title="Recent Activity" testId="section-activity">
            {activities.length === 0 ? <EmptyRow text="No recent activity yet." /> : (
              <ol className="space-y-3">
                {activities.slice(0, 15).map((a) => {
                  const actionColor = a.action_type === 'CREATE' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
                    : a.action_type === 'UPDATE' ? 'text-blue-400 bg-blue-500/10 border-blue-500/30'
                    : 'text-red-400 bg-red-500/10 border-red-500/30';
                  const verb = a.action_type === 'CREATE' ? 'added' : a.action_type === 'UPDATE' ? 'updated' : 'deleted';
                  const roleTint = a.actor_role === 'super_admin' || a.actor_role === 'admin' ? 'text-amber-400' : 'text-emerald-400';

                  // Compute a compact "what changed" diff (skips 'updated_at')
                  let changes = [];
                  if (a.previous_data && a.new_data && typeof a.previous_data === 'object' && typeof a.new_data === 'object') {
                    const keys = new Set([...Object.keys(a.previous_data), ...Object.keys(a.new_data)]);
                    keys.forEach(k => {
                      if (k === 'updated_at') return;
                      const b = a.previous_data[k];
                      const n = a.new_data[k];
                      if (JSON.stringify(b) !== JSON.stringify(n)) {
                        changes.push({ k, b, n });
                      }
                    });
                  }
                  return (
                    <li key={a.id} className="flex items-start gap-3" data-testid={`activity-${a.id}`}>
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center ${actionColor}`}>
                        <Activity className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-200">
                          <span className={`font-medium ${roleTint}`}>{a.actor_name}</span>
                          {' '}
                          <span className="text-slate-500">
                            ({a.actor_role === 'super_admin' || a.actor_role === 'admin' ? 'Admin' : 'Client'})
                          </span>
                          {' '}
                          <span className="text-slate-400">{verb}</span>
                          {' '}
                          <span className="text-white">{a.entity_type}</span>
                          {a.entity_name && (
                            <span className="text-emerald-300"> · {a.entity_name}</span>
                          )}
                        </p>
                        {changes.length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {changes.slice(0, 4).map(({ k, b, n }) => (
                              <li key={k} className="text-[11px] text-slate-400 leading-relaxed">
                                <span className="text-slate-500">{k.replace(/_/g, ' ')}:</span>{' '}
                                {b !== undefined && b !== null && b !== '' ? (
                                  <span className="text-slate-500 line-through">{String(b).slice(0, 40)}</span>
                                ) : <span className="text-slate-600 italic">empty</span>}
                                <span className="text-slate-600 mx-1">→</span>
                                {n !== undefined && n !== null && n !== '' ? (
                                  <span className="text-emerald-300">{String(n).slice(0, 40)}</span>
                                ) : <span className="text-slate-600 italic">empty</span>}
                              </li>
                            ))}
                            {changes.length > 4 && (
                              <li className="text-[11px] text-slate-500 italic">+ {changes.length - 4} more field(s)</li>
                            )}
                          </ul>
                        )}
                        <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {fmtRelative(a.timestamp)}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </SectionCard>
        </div>

        {/* Right sidebar */}
        <div className="lg:col-span-1 space-y-5">
          {/* Recent Orders */}
          <div className="bg-[#0f1611] border border-emerald-900/30 rounded-xl p-5" data-testid="recent-orders-widget">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingCart className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-serif text-white">Recent Orders</h3>
            </div>
            {recentOrders.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-3">No orders yet</p>
            ) : (
              <div className="space-y-2">
                {recentOrders.map((o) => (
                  <Link key={o.id} to={`/orders/${o.id}`}
                    className="block p-3 bg-[#080b09] border border-emerald-900/20 rounded-lg hover:border-emerald-500/40 transition-colors" data-testid={`recent-order-${o.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{o.display_name || o.product?.name || 'Order'}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{fmtDate(o.created_at)}</p>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 flex-shrink-0">
                        {o.status?.replace(/_/g, ' ') || 'draft'}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            {companyOrders.length > 4 && (
              <button onClick={() => navigate(`/dashboard?tab=orders&company=${company.id}`)}
                className="mt-3 w-full text-sm text-emerald-400 hover:text-emerald-300 flex items-center justify-center gap-1" data-testid="view-all-orders">
                View All Orders <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-[#0f1611] border border-emerald-900/30 rounded-xl p-5" data-testid="quick-actions-widget">
            <h3 className="text-lg font-serif text-white mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button onClick={() => setDirModal({})} className="w-full text-left px-4 py-2.5 bg-[#080b09] border border-emerald-900/20 rounded-lg text-sm text-white hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-colors flex items-center gap-2" data-testid="qa-add-director">
                <UserPlus className="w-4 h-4 text-emerald-400" /> Add Director
              </button>
              <button onClick={() => setShModal({})} className="w-full text-left px-4 py-2.5 bg-[#080b09] border border-emerald-900/20 rounded-lg text-sm text-white hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-colors flex items-center gap-2" data-testid="qa-add-shareholder">
                <UserPlus className="w-4 h-4 text-emerald-400" /> Add Shareholder
              </button>
              <button onClick={() => setCredModal({})} className="w-full text-left px-4 py-2.5 bg-[#080b09] border border-emerald-900/20 rounded-lg text-sm text-white hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-colors flex items-center gap-2" data-testid="qa-add-credential">
                <KeyRound className="w-4 h-4 text-emerald-400" /> Add Credential
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {dirModal !== null && <DirectorModal director={dirModal} entityCfg={entityConfig(company?.entity_type)} onSave={saveDirector} onClose={() => setDirModal(null)} saving={modalSaving} />}
      {shModal !== null && <ShareholderModal sh={shModal} directors={directors} entityCfg={entityConfig(company?.entity_type)} onSave={saveShareholder} onClose={() => setShModal(null)} saving={modalSaving} />}
      {credModal !== null && <CredentialModal cred={credModal} companyId={companyId} onSave={saveCredential} onClose={() => setCredModal(null)} saving={modalSaving} />}
      {companyEdit && <CompanyEditModal company={company} section={companyEdit.section} onSave={saveCompany} onClose={() => setCompanyEdit(null)} saving={modalSaving} />}
      {renewalDir && (
        <Modal title={`Request ${renewalType.toUpperCase()} Renewal`} onClose={() => setRenewalDir(null)} testId="renewal-modal">
          <div className="p-5 space-y-3">
            <p className="text-sm text-slate-300">
              Submit a {renewalType.toUpperCase()} renewal request to the admin team for{' '}
              <span className="text-white font-medium">{renewalDir.full_name}</span>.
            </p>
            <div className="p-3 rounded-lg bg-[#080b09] border border-emerald-900/30 text-xs text-slate-400 space-y-1">
              <div>Director: <span className="text-slate-200">{renewalDir.full_name}</span></div>
              <div>Current DSC Status: <StatusBadge status={renewalDir.dsc_status} type="dsc" /></div>
              <div>Current DIN Status: <StatusBadge status={renewalDir.din_status} type="din" /></div>
              {renewalDir.dsc_expiry_date && <div>DSC Expiry: <span className="text-slate-200">{fmtDate(renewalDir.dsc_expiry_date)}</span></div>}
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Additional Notes (optional)</label>
              <textarea value={renewalMsg} onChange={(e) => setRenewalMsg(e.target.value)}
                placeholder="Any specific requirements or urgency?"
                rows={3}
                className="w-full bg-[#080b09] border border-emerald-900/30 rounded-md text-white text-sm p-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="renewal-message" />
            </div>
            <p className="text-[11px] text-slate-500">Your admin will receive this in the Leads tab and follow up with next steps and pricing.</p>
          </div>
          <div className="p-5 border-t border-emerald-900/40 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRenewalDir(null)} className="border-slate-700 text-slate-300 bg-transparent hover:bg-slate-800">Cancel</Button>
            <Button onClick={submitRenewal} disabled={submittingRenewal}
              className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium" data-testid="submit-renewal-btn">
              {submittingRenewal ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Send Request
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CompanyDetailView;
